import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import * as services from "../../services/saleng";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import UserFormDialog from "../users/UserFormDialog";
import {
  ADMIN_ROLE_TITLES,
  DATABASE_ONLY_ADMIN_ROLE_TITLES,
} from "../../auth/adminAccess";

const SHOP_TYPES = [
  { value: "FRANCHISE", label: "แฟรนไชส์" },
  { value: "OWN_SHOP", label: "ร้านเดี่ยว" },
];

const SuperAdmin = () => {
  const [shops, setShops] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shopDialogOpen, setShopDialogOpen] = useState(false);
  const [editShopDialogOpen, setEditShopDialogOpen] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [editingShop, setEditingShop] = useState(null);
  const [shopName, setShopName] = useState("");
  const [shopDetail, setShopDetail] = useState("");
  const [editShopName, setEditShopName] = useState("");
  const [editShopDetail, setEditShopDetail] = useState("");
  const [shopType, setShopType] = useState("OWN_SHOP");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [shopsResponse, rolesResponse] = await Promise.all([
        services.getAllShops(),
        services.getRoles(),
      ]);
      setShops(shopsResponse.data.data || []);
      setRoles(rolesResponse.data.data || []);
    } catch (error) {
      console.error("Super Admin data loading failed:", error);
      Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถโหลดข้อมูลร้านได้", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const roleOptions = useMemo(() => roles, [roles]);
  const adminRoleOptions = useMemo(
    () =>
      roles.filter(
        (role) =>
          ADMIN_ROLE_TITLES.has(role.title) &&
          !DATABASE_ONLY_ADMIN_ROLE_TITLES.has(role.title),
      ),
    [roles],
  );

  const handleCreateShop = async () => {
    if (!shopName.trim()) return;
    try {
      await services.createShopForAdmin({
        name: shopName.trim(),
        ...(shopDetail.trim() ? { detail: shopDetail.trim() } : {}),
        shopType,
      });
      setShopDialogOpen(false);
      setShopName("");
      setShopDetail("");
      setShopType("OWN_SHOP");
      await fetchData();
      Swal.fire("สำเร็จ", "เพิ่มร้านเรียบร้อยแล้ว", "success");
    } catch (error) {
      console.error("Shop creation failed:", error);
      Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถเพิ่มร้านได้", "error");
    }
  };

  const handleOpenEditShop = (shop) => {
    setEditingShop(shop);
    setEditShopName(shop.name || "");
    setEditShopDetail(shop.detail || "");
    setEditShopDialogOpen(true);
  };

  const handleUpdateShop = async () => {
    if (!editingShop || !editShopName.trim()) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "ยืนยันการแก้ไขข้อมูลร้าน?",
      text: "ชื่อร้านและรายละเอียดจะถูกเปลี่ยนแปลง",
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#185519",
      cancelButtonColor: "#d33",
      customClass: { container: "my-swal" },
    });

    if (!result.isConfirmed) return;

    try {
      const response = await services.updateShopForAdmin(editingShop.id, {
        name: editShopName.trim(),
        detail: editShopDetail.trim(),
      });
      const updatedShop = response.data.data;
      setShops((current) =>
        current.map((shop) =>
          shop.id === editingShop.id ? { ...shop, ...updatedShop } : shop,
        ),
      );
      setEditShopDialogOpen(false);
      setEditingShop(null);
      Swal.fire("สำเร็จ", "แก้ไขข้อมูลร้านเรียบร้อยแล้ว", "success");
    } catch (error) {
      console.error("Shop update failed:", error);
      Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถแก้ไขข้อมูลร้านได้", "error");
    }
  };

  const handleShopTypeChange = async (shop, nextShopType) => {
    if (shop.shopType === nextShopType) return;

    const nextLabel =
      SHOP_TYPES.find((type) => type.value === nextShopType)?.label ||
      nextShopType;
    const result = await Swal.fire({
      icon: "warning",
      title: "ยืนยันการเปลี่ยนประเภท Shop?",
      text: `ต้องการเปลี่ยนร้าน ${shop.name || shop.id} เป็น “${nextLabel}” หรือไม่`,
      showCancelButton: true,
      confirmButtonText: "ยืนยัน",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#185519",
      cancelButtonColor: "#d33",
      customClass: { container: "my-swal" },
    });

    if (!result.isConfirmed) return;

    const previous = shop.shopType;
    setShops((current) =>
      current.map((item) =>
        item.id === shop.id ? { ...item, shopType: nextShopType } : item,
      ),
    );
    try {
      await services.updateShopForAdmin(shop.id, { shopType: nextShopType });
    } catch (error) {
      console.error("Shop type update failed:", error);
      setShops((current) =>
        current.map((item) =>
          item.id === shop.id ? { ...item, shopType: previous } : item,
        ),
      );
      Swal.fire("เกิดข้อผิดพลาด", "ไม่สามารถเปลี่ยนประเภทร้านได้", "error");
    }
  };

  const columns = [
    { field: "id", headerName: "รหัสร้าน", width: 75 },
    { field: "name", headerName: "ชื่อร้าน", flex: 1, minWidth: 150 },
    {
      field: "detail",
      headerName: "รายละเอียด",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => row.detail || "-",
    },
    {
      field: "shopType",
      headerName: "ประเภท Shop",
      flex: 1,
      minWidth: 155,
      renderCell: ({ row }) => (
        <FormControl size="small" fullWidth>
          <Select
            value={row.shopType || "OWN_SHOP"}
            onChange={(event) =>
              handleShopTypeChange(row, event.target.value)
            }
          >
            {SHOP_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
    },
    {
      field: "branchesCount",
      headerName: "จำนวนสาขา",
      width: 100,
      valueGetter: (_value, row) => row._count?.branches ?? row.branches?.length ?? 0,
    },
    {
      field: "usersCount",
      headerName: "จำนวน User",
      width: 100,
      valueGetter: (_value, row) => row._count?.users ?? row.users?.length ?? 0,
    },
    {
      field: "actions",
      headerName: "จัดการ",
      width: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            color="primary"
            title="แก้ไขชื่อและรายละเอียดร้าน"
            onClick={() => handleOpenEditShop(row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddIcon />}
            disabled={!row.branches?.length}
            title={
              row.branches?.length
                ? "เพิ่ม User ให้ร้านนี้"
                : "ร้านนี้ยังไม่มีสาขา"
            }
            onClick={() => {
              setSelectedShop(row);
              setUserDialogOpen(true);
            }}
          >
            เพิ่ม User
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        px: { xs: 0, md: 1 },
        pb: 2,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <AppBreadcrumbs />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1,
          mb: 1.5,
          gap: 2,
        }}
      >
        <Box>
          <Typography component="h2" variant="h5">
            จัดการร้าน
          </Typography>
          <Typography color="text.secondary">
            จัดการร้านค้าทั้งหมดในระบบ
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<StorefrontIcon />}
          onClick={() => setShopDialogOpen(true)}
        >
          เพิ่มร้าน
        </Button>
      </Box>

      <Paper sx={{ width: "100%", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rows={shops}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{
            "& .MuiDataGrid-columnHeaders": { minHeight: 44 },
            "& .MuiDataGrid-cell": { py: 0.5 },
          }}
        />
      </Paper>

      <Dialog
        open={shopDialogOpen}
        onClose={() => setShopDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>เพิ่มร้าน</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}
        >
          <TextField
            label="ชื่อร้าน"
            value={shopName}
            onChange={(event) => setShopName(event.target.value)}
            required
            autoFocus
          />
          <TextField
            label="รายละเอียด"
            value={shopDetail}
            onChange={(event) => setShopDetail(event.target.value)}
            multiline
            minRows={2}
          />
          <TextField
            select
            label="ประเภท Shop"
            value={shopType}
            onChange={(event) => setShopType(event.target.value)}
          >
            {SHOP_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="body2" color="text.secondary">
            ระบบจะสร้าง “สาขาหลัก” ให้ร้านนี้อัตโนมัติ และไม่สามารถแก้ไขหรือลบได้
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 1 }}>
          <Button onClick={() => setShopDialogOpen(false)}>ยกเลิก</Button>
          <Button
            variant="contained"
            onClick={handleCreateShop}
            disabled={!shopName.trim()}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editShopDialogOpen}
        onClose={() => setEditShopDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>แก้ไขข้อมูลร้าน</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}
        >
          <TextField
            label="ชื่อร้าน"
            value={editShopName}
            onChange={(event) => setEditShopName(event.target.value)}
            required
            autoFocus
          />
          <TextField
            label="รายละเอียด"
            value={editShopDetail}
            onChange={(event) => setEditShopDetail(event.target.value)}
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions sx={{ pt: 1 }}>
          <Button onClick={() => setEditShopDialogOpen(false)}>ยกเลิก</Button>
          <Button
            variant="contained"
            onClick={handleUpdateShop}
            disabled={!editShopName.trim()}
          >
            บันทึก
          </Button>
        </DialogActions>
      </Dialog>

      {selectedShop && (
        <UserFormDialog
          open={userDialogOpen}
          shopId={selectedShop.id}
          branchOptions={selectedShop.branches || []}
          roles={roleOptions}
          adminRoles={adminRoleOptions}
          canManageAdminRole
          isShopScoped
          onClose={() => {
            setUserDialogOpen(false);
            setSelectedShop(null);
          }}
          onSuccess={fetchData}
        />
      )}
    </Box>
  );
};

export default SuperAdmin;
